/**
 * Contratos del simulador de conectores (ADR-15, brief
 * `simulador-conectores.md`): la sección del Studio que hace las mismas
 * peticiones que hará el asesor, sin asesor por medio, y enseña lo que
 * vuelve. Todo cuelga de `/studio/simulador/`, así que entra por el mismo
 * bearer de Entra que el resto del Studio: no hay puerta nueva.
 *
 * Por qué dos idiomas en las claves:
 * - El sobre (catálogo, escenarios, descubrimiento, cuerpos de petición)
 *   va en español, como el resto del Studio (`nombre`, `estado`).
 * - Lo que sale del puerto (`ConceptAvailability`, `ConceptResult`,
 *   `Freshness`) viaja con SU forma, en inglés: la vista «normalizado» ha
 *   de ser exactamente lo que recibirá el asesor; traducir sus claves la
 *   falsearía. La API comprueba en compilación que el tipo del paquete
 *   `@skein/connectors` encaja en estos esquemas (si el puerto cambia, la
 *   API deja de compilar).
 *
 * Este paquete no importa `@skein/connectors` a propósito: lo usa el
 * navegador y ese paquete trae lectura de disco.
 *
 * Nada de esto se persiste: ni credenciales ni resultados (ADR-15). El
 * `tokenFuente` viaja en el cuerpo de cada petición y la API no lo
 * devuelve nunca.
 */
import { z } from "zod";

// ── Vocabulario del puerto (espejo de @skein/connectors/contract.ts) ──

/** ADITIVO (ADR-22): `document`, el adaptador documental; el simulador no lo conecta, pero el vocabulario es el del puerto. */
export const SIMULADOR_SOURCE_TYPES = ["fixture", "business-central", "power-bi", "document"] as const;
/** Tipos de origen con recetas en fichero en el repo (`fixture` las deriva de la cabecera de cada `sources/*.md`). */
export const SIMULADOR_REPO_SOURCE_TYPES = ["business-central", "power-bi"] as const;
export const SIMULADOR_RESULT_STATES = ["ok", "stale", "unsupported_concept", "missing_dimension", "permission_denied", "connection_expired", "source_error", "period_not_covered"] as const;
export const SIMULADOR_GRANULARITIES = ["month", "quarter", "year"] as const;

const SourceType = z.enum(SIMULADOR_SOURCE_TYPES);
const RepoSourceType = z.enum(SIMULADOR_REPO_SOURCE_TYPES);
const Granularity = z.enum(SIMULADOR_GRANULARITIES);
const Unit = z.enum(["currency", "days", "count", "percentage"]);
const Sign = z.enum(["magnitude", "signed"]);
const Temporal = z.enum(["flow", "balance"]);
const Lifecycle = z.enum(["draft", "published", "retired"]);
const Slug = z.string().regex(/^[a-z][a-z0-9_]*$/, "identificador en snake_case");
const IsoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "fecha YYYY-MM-DD");

/** Carpeta del banco: `SC-001-descuento-cliente-grande-que-paga-tarde`. Nada de rutas: solo el nombre. */
export const ESCENARIO_ID = /^SC-\d{3}-[a-z0-9-]+$/;
/** Tope del texto de una receta editada (64 KB en UTF-8): una receta es un documento corto. */
export const RECETA_MAX_BYTES = 64 * 1024;
/** Tope del token delegado: un JWT de Entra ronda los 2–4 KB. */
export const TOKEN_FUENTE_MAX = 16 * 1024;

// ── Catálogo ──────────────────────────────────────────────────────────

/** Versión y estado de la receta del repo vigente para un tipo de origen (la que usaría el simulador: published o la draft más alta). */
export const RecetaRepoRef = z.object({ version: z.number().int().positive(), estado: Lifecycle });

export const ConceptoCatalogo = z.object({
  id: Slug,
  version: z.number().int().positive(),
  estado: Lifecycle,
  clase: z.enum(["base", "derived"]),
  etiqueta: z.string(),
  definicion: z.string(),
  nivel: z.enum(["pulse", "profitability", "working_capital_risk"]),
  temporal: Temporal,
  unidad: Unit,
  signo: Sign,
  dimensiones: z.array(z.string()),
  granularidades: z.array(Granularity),
  /** Solo los derivados (reservado: OQ-18). */
  dependeDe: z.array(Slug),
  /** Por tipo de origen con recetas en el repo: la vigente, o null si no hay. */
  recetas: z.object({ "business-central": RecetaRepoRef.nullable(), "power-bi": RecetaRepoRef.nullable() }),
});
export const CatalogoSimulador = z.object({
  versionCatalogo: z.number().int().nonnegative(),
  conceptos: z.array(ConceptoCatalogo),
});

// ── Escenarios del banco (origen `fixture`) ───────────────────────────

export const EscenarioSimulador = z.object({
  id: z.string().regex(ESCENARIO_ID),
  /** El título de `conversation.md` («SC-001 · El cliente que más vende…»); el id si no lo hay. */
  nombre: z.string(),
  /** La empresa de la cabecera de sus `sources/*.md`, si la dicen. */
  empresa: z.string().nullable(),
  ficheros: z.array(z.string()),
});
export const EscenariosSimulador = z.object({ escenarios: z.array(EscenarioSimulador) });

// ── Receta del repo ───────────────────────────────────────────────────

/**
 * La receta parseada, con su formato de fichero (claves en inglés: es el
 * formato de `content/connectors/recipes/`). El esquema completo vive en
 * `@skein/connectors` (mapping.ts) y la API valida con él; aquí basta la
 * forma para tiparla en el cliente.
 */
export const RecetaParseada = z
  .object({
    format: z.literal(1),
    concept: Slug,
    concept_version: z.number().int().positive(),
    source_type: SourceType,
    scope: z.literal("standard"),
    version: z.number().int().positive(),
    status: Lifecycle,
    implements: z.string(),
    request: z.record(z.string(), z.unknown()),
    normalize: z.record(z.string(), z.unknown()),
    notes: z.string().optional(),
  })
  .passthrough();

export const RecetaRepo = z.object({
  tipo: RepoSourceType,
  concepto: Slug,
  /** Ruta dentro de `content/connectors/` (`recipes/business-central/revenue.yaml`). */
  fichero: z.string(),
  /** El YAML tal cual está en el repo, comentarios incluidos: lo que abre el editor. */
  texto: z.string(),
  receta: RecetaParseada,
});

// ── El origen: con qué se configura el adaptador ──────────────────────

/**
 * El origen es configuración del adaptador, fuera del puerto (ADR-15):
 * sale del descubrimiento y nombra la procedencia. Unión discriminada por
 * `tipo`; Power BI llega en la v2 del simulador.
 */
/** Nombre de entorno de Business Central (el mismo patrón que exige el adaptador): así un entorno mal escrito es 400 aquí y no un fallo de la fuente. */
export const ENTORNO_BC = /^[A-Za-z0-9][A-Za-z0-9_-]{0,99}$/;
export const OrigenSimulador = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("fixture"), escenario: z.string().regex(ESCENARIO_ID, "escenario del banco: SC-NNN-nombre") }).strict(),
  z
    .object({
      tipo: z.literal("business-central"),
      entorno: z.string().regex(ENTORNO_BC, "nombre de entorno de Business Central"),
      empresaId: z.string().min(1).max(64),
      empresaNombre: z.string().min(1).max(200),
      /** Moneda local de la empresa (ISO 4217), de `listCompanies`; sin ella el resultado sale sin moneda. */
      moneda: z.string().regex(/^[A-Z]{3}$/).optional(),
    })
    .strict(),
]);

/** El token delegado de Business Central (audiencia BC, lo pide la SPA con MSAL). Entra, se usa y se olvida. */
const TokenFuente = z.string().min(1).max(TOKEN_FUENTE_MAX);

// ── Descubrimiento de Business Central (fuera del puerto) ─────────────

export const FalloFuente = z.object({
  estado: z.enum(["connection_expired", "permission_denied", "source_error"]),
  mensaje: z.string(),
  /** Lo que devolvió la fuente (cuerpo de error OData, texto) o null. */
  enBruto: z.unknown(),
});

export const EntornosPeticion = z.object({
  tokenFuente: TokenFuente,
  entornosManuales: z.array(z.string().max(100)).max(20).optional(),
});
export const EntornoBC = z.object({
  nombre: z.string(),
  tipo: z.string().nullable(),
  pais: z.string().nullable(),
  familia: z.string().nullable(),
  estado: z.string().nullable(),
  /** `listed`: vino de la fuente; `manual`: lo escribió el usuario. */
  procedencia: z.enum(["listed", "manual"]),
});
export const EntornosRespuesta = z.object({
  entornos: z.array(EntornoBC),
  /** true si la fuente dio la lista; false si solo están los escritos a mano. */
  listado: z.boolean(),
  fallo: FalloFuente.optional(),
});

export const EmpresasPeticion = z.object({ tokenFuente: TokenFuente, entorno: z.string().regex(ENTORNO_BC, "nombre de entorno de Business Central") });
export const EmpresaBC = z.object({
  id: z.string(),
  nombre: z.string(),
  nombreVisible: z.string(),
  /** Moneda local (de `companyInformation`); null si no se pudo leer. */
  moneda: z.string().nullable(),
});
/** Si la fuente falla, `empresas` va vacía y `fallo` dice por qué (el 401 de BC es `connection_expired`). */
export const EmpresasRespuesta = z.object({ empresas: z.array(EmpresaBC), fallo: FalloFuente.optional() });

// ── Lo que sale del puerto, con su forma (claves en inglés) ───────────

export const OrigenPuerto = z.object({ label: z.string(), ids: z.record(z.string(), z.string()) });
export const PeriodoPuerto = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("period"), start: z.string(), end: z.string() }),
  z.object({ kind: z.literal("date"), date: z.string() }),
]);

/** `ConceptAvailability` del puerto (`listConcepts`). */
export const ConceptAvailabilityWire = z.object({
  concept: z.string(),
  version: z.number().int(),
  status: z.enum(["draft", "published"]),
  label: z.string(),
  temporal: Temporal,
  unit: Unit,
  dimensions: z.array(z.string()),
  granularities: z.array(Granularity),
  /** Lo que la conexión cubre del concepto (ADR-18); null si el adaptador no lo declara (una fuente viva). Opcional: una API anterior no lo manda. */
  coverage: z.object({ period: PeriodoPuerto, fixed: z.boolean(), /** ADITIVO (ADR-22): los tramos, si hay huecos. */ ranges: z.array(PeriodoPuerto).optional() }).nullable().optional(),
  mapping: z.object({ id: z.string(), version: z.number().int() }),
});

/** `ConceptResult` del puerto: la vista «normalizado», lo que recibirá el asesor. */
export const ConceptResultWire = z.object({
  concept: z.string(),
  conceptVersion: z.number().int(),
  status: z.enum(SIMULADOR_RESULT_STATES),
  period: PeriodoPuerto.nullable(),
  rows: z.array(
    z.object({
      period: z.string().optional(),
      dimensions: z.record(z.string(), z.string()),
      value: z.number(),
    }),
  ),
  unit: Unit,
  currency: z.string().optional(),
  sign: Sign,
  dataDate: z.string().nullable(),
  provenance: z
    .object({
      sourceType: SourceType,
      origin: OrigenPuerto,
      mapping: z.object({ id: z.string(), version: z.number().int() }),
      file: z.object({ path: z.string(), date: z.string() }).optional(),
      /** ADITIVO (ADR-22 pt 10): los documentos de cifras que aportaron cubos. */
      documents: z
        .array(z.object({ id: z.string(), name: z.string(), hash: z.string(), cutOffDate: z.string(), file: z.string(), table: z.string(), conceptVersion: z.number().int() }))
        .optional(),
    })
    .nullable(),
  message: z.string().optional(),
  /** Solo en `period_not_covered`: lo que la fuente sí cubre y con qué desglose (ADR-18). */
  covered: z.object({ period: PeriodoPuerto, granularities: z.array(Granularity), /** ADITIVO (ADR-22 pt 2): los tramos, si hay huecos. */ ranges: z.array(PeriodoPuerto).optional() }).optional(),
});

// ── Conceptos que sirve un origen ─────────────────────────────────────

export const ConceptosPeticion = z.object({ origen: OrigenSimulador });

/**
 * Lo que cubren los datos de un concepto servido: `[inicio, fin]` y si es
 * de periodo fijo (`fija`: solo se pide entero, sin desglose). Va en el
 * sobre y no en `ConceptAvailabilityWire`, que es la forma del puerto.
 */
export const CoberturaConcepto = z.object({ inicio: IsoDay, fin: IsoDay, fija: z.boolean() });
export const ConceptosRespuesta = z.object({
  /** El origen tal como quedó configurado el adaptador (lo que nombrará la procedencia). */
  origen: z.object({ tipo: SourceType }).merge(OrigenPuerto),
  conceptos: z.array(ConceptAvailabilityWire),
  /**
   * Por concepto, lo que cubre el origen. Solo con `fixture` (un escenario
   * es fijo: sus periodos se anclan a sus datos, no a hoy). Opcional a
   * propósito: el Studio y la API se despliegan por separado, y cada uno ha
   * de funcionar con la versión anterior del otro.
   */
  cobertura: z.record(z.string(), CoberturaConcepto).optional(),
  /**
   * La conexión no pudo decir qué sirve (ADR-19): `conceptos` va vacía y
   * esto dice por qué. Una lista vacía SIN `fallo` significa que el origen
   * no sirve ningún concepto.
   */
  fallo: z.object({ estado: FalloFuente.shape.estado, mensaje: z.string() }).optional(),
});

// ── Ejecutar: las tres vistas ─────────────────────────────────────────

const byteLength = (s: string): number => new TextEncoder().encode(s).length;

export const ConsultaSimulador = z
  .object({
    concepto: Slug,
    /** Flujos: periodo cerrado [inicio, fin]. */
    periodo: z.object({ inicio: IsoDay, fin: IsoDay }).strict().optional(),
    /** Saldos: una fecha. */
    fecha: IsoDay.optional(),
    granularidad: Granularity.optional(),
    dimensiones: z.array(z.string().max(64)).max(10).optional(),
    filtros: z.record(z.string().max(64), z.string().max(200)).optional(),
  })
  .strict()
  .refine((c) => (c.periodo === undefined) !== (c.fecha === undefined), "la consulta lleva un periodo (flujo) o una fecha (saldo), no los dos ni ninguno");

export const EjecutarPeticion = z
  .object({
    origen: OrigenSimulador,
    /** Obligatorio con Business Central (sin él, el resultado es `connection_expired`); ignorado con `fixture`. */
    tokenFuente: TokenFuente.optional(),
    consulta: ConsultaSimulador,
    /** La receta editada en la pantalla, en YAML. Sustituye a la del repo SOLO en esta llamada; no se guarda. */
    receta: z.string().refine((s) => byteLength(s) <= RECETA_MAX_BYTES, `la receta pasa de ${RECETA_MAX_BYTES / 1024} KB`).optional(),
  })
  .strict();

export const EjecutarRespuesta = z.object({
  /** Vista «petición»: lo que salió hacia la fuente, sin secretos (el bearer, recortado). null si no se llegó a pedir. */
  peticion: z.object({ resumen: z.string(), detalle: z.unknown() }).nullable(),
  /** Vista «en bruto»: lo que devolvió la fuente, tal cual. */
  enBruto: z.unknown(),
  /** Vista «normalizado». */
  resultado: ConceptResultWire,
  /** La receta que se USÓ, serializada en YAML; null si no se llegó a resolver ninguna (concepto fuera del catálogo, sin receta). */
  receta: z
    .object({
      texto: z.string(),
      editada: z.boolean(),
      /** `repo`: fichero del repo · `editada`: la de esta petición · `derivada`: de la cabecera de un `sources/*.md` (fixture). */
      origen: z.enum(["repo", "editada", "derivada"]),
    })
    .nullable(),
  /** `checkFreshness` del concepto, tras la consulta (va aquí y no en ruta propia: ver la API). */
  frescura: z.object({
    dataDate: z.string().nullable(),
    stale: z.boolean(),
    /** `ok`, o el fallo de conexión que impidió saberlo (ADR-19). Opcional: una API anterior no lo manda. */
    estado: z.enum(["ok", "connection_expired", "permission_denied", "source_error"]).optional(),
    mensaje: z.string().optional(),
  }),
});

// ── Rutas ─────────────────────────────────────────────────────────────

/** Rutas del simulador — el mapa único que comparten API y cliente. */
export const SIMULADOR_ROUTES = {
  catalogo: "/studio/simulador/catalogo",
  escenarios: "/studio/simulador/escenarios",
  receta: (tipo: string, concepto: string) => `/studio/simulador/recetas/${encodeURIComponent(tipo)}/${encodeURIComponent(concepto)}`,
  /** Plantilla para registrar la ruta en Fastify. */
  recetaPattern: "/studio/simulador/recetas/:tipo/:concepto",
  entornos: "/studio/simulador/business-central/entornos",
  empresas: "/studio/simulador/business-central/empresas",
  conceptos: "/studio/simulador/conceptos",
  ejecutar: "/studio/simulador/ejecutar",
  /** El explorador (I-18): pregunta libre a un agente sobre un Bridge de solo lectura. */
  explorar: "/studio/simulador/explorar",
} as const;

export type RecetaRepoRef = z.infer<typeof RecetaRepoRef>;
export type ConceptoCatalogo = z.infer<typeof ConceptoCatalogo>;
export type CatalogoSimulador = z.infer<typeof CatalogoSimulador>;
export type EscenarioSimulador = z.infer<typeof EscenarioSimulador>;
export type EscenariosSimulador = z.infer<typeof EscenariosSimulador>;
export type RecetaParseada = z.infer<typeof RecetaParseada>;
export type RecetaRepo = z.infer<typeof RecetaRepo>;
export type OrigenSimulador = z.infer<typeof OrigenSimulador>;
export type FalloFuente = z.infer<typeof FalloFuente>;
export type EntornosPeticion = z.infer<typeof EntornosPeticion>;
export type EntornoBC = z.infer<typeof EntornoBC>;
export type EntornosRespuesta = z.infer<typeof EntornosRespuesta>;
export type EmpresasPeticion = z.infer<typeof EmpresasPeticion>;
export type EmpresaBC = z.infer<typeof EmpresaBC>;
export type EmpresasRespuesta = z.infer<typeof EmpresasRespuesta>;
export type ConceptAvailabilityWire = z.infer<typeof ConceptAvailabilityWire>;
export type ConceptResultWire = z.infer<typeof ConceptResultWire>;
export type ConceptosPeticion = z.infer<typeof ConceptosPeticion>;
export type CoberturaConcepto = z.infer<typeof CoberturaConcepto>;
export type ConceptosRespuesta = z.infer<typeof ConceptosRespuesta>;
export type ConsultaSimulador = z.infer<typeof ConsultaSimulador>;
/** Lo que manda el cliente (con los opcionales sin rellenar). */
export type EjecutarPeticion = z.input<typeof EjecutarPeticion>;
export type EjecutarRespuesta = z.infer<typeof EjecutarRespuesta>;
